// ---------------------------------------------------------------------------------------------
// HER MEMBERSHIP PAGE — **HER ARTWORK IS THE PAGE, AND THE LADDER IS PAINTED INTO IT.**
//
// Her order (conversion surface 3 of three): *"her `membership.png` / `membership-vertical.png` AS
// THE PAGE; TIER-AWARE BUTTONS -- desktop in her reserved spaces, MOBILE THE TIER CARDS ARE THE
// BUTTONS, with the CURRENTLY SUBSCRIBED badge (gold on plum) on the member's own tier; THREE REVIEW
// SLOTS FADING through the approved pool, GOLD, IN QUOTATIONS, staggered; every membership trigger
// routing here, the OLD POPUP RETIRED, SPIRIT KEEPING ABSENT."*
//
// >>> EVERY RECTANGLE BELOW WAS READ OFF HER PIXELS (R25/R180). <<< A probe scanned both masters for
// the hairlines her cards and review slots are drawn with, and these are those edges as PERCENTAGES
// of her artwork's own box. Her desktop master is 1672x941 and her phone master 941x1672:
//
//   DESKTOP  cards y 24.44%..71.15% | Newbie x 18.48..41.57 | Apprentice 42.58..62.44 | Vet 63.46..82.60
//            review slots y 76.20%..87.35% | 19.44..39.35 | 40.49..60.11 | 61.36..81.10
//   PHONE    cards x 21.04..80.13 | Newbie y 14.59..42.76 | Apprentice 43.70..61.12 | Vet 62.02..82.42
//            review slots y 85.60%..92.60% | 5.63..33.48 | 35.18..63.23 | 64.82..93.41
//
// **THE TIER LADDER IS PAINTED -- every name, every price and every feature line is in her artwork**
// -- so nothing here rebuilds any of it as type (the mockup-matching law). What the app adds is the
// one thing she left space for: a control per tier, and the words in her three review slots.
//
// It imports NOTHING, which is the entry requirement that lets `scripts/membership-page.mjs` LOAD
// and RUN every line of it with no browser (R146: reach is transitive).

/**
 * >>> HER RESERVED SPACES. <<<
 *
 * On DESKTOP her cards are painted with empty space at the foot of each one, and that is where the
 * control goes. On a PHONE she ruled that **THE CARD IS THE BUTTON**, so the target is the whole
 * card rather than a strip inside it -- which is also why the two maps are shaped differently rather
 * than one being the other scaled.
 */
export const DESKTOP_TIERS = Object.freeze([
  Object.freeze({ tier: "newbie", card: Object.freeze({ left: 18.48, top: 24.44, width: 23.09, height: 46.71 }), control: Object.freeze({ left: 20.5, top: 63.4, width: 19.0, height: 6.2 }) }),
  Object.freeze({ tier: "apprentice", card: Object.freeze({ left: 42.58, top: 24.44, width: 19.86, height: 46.71 }), control: Object.freeze({ left: 44.4, top: 63.4, width: 16.2, height: 6.2 }) }),
  Object.freeze({ tier: "vet", card: Object.freeze({ left: 63.46, top: 24.44, width: 19.14, height: 46.71 }), control: Object.freeze({ left: 65.3, top: 63.4, width: 15.5, height: 6.2 }) }),
]);

export const PHONE_TIERS = Object.freeze([
  Object.freeze({ tier: "newbie", card: Object.freeze({ left: 21.04, top: 14.59, width: 59.09, height: 28.17 }), control: null }),
  Object.freeze({ tier: "apprentice", card: Object.freeze({ left: 21.04, top: 43.70, width: 59.09, height: 17.42 }), control: null }),
  Object.freeze({ tier: "vet", card: Object.freeze({ left: 21.04, top: 62.02, width: 59.09, height: 20.40 }), control: null }),
]);

/**
 * >>> THE SLOT BOX IS THE CLEAN GAP BETWEEN HER PAINTED QUOTE MARK AND HER PAINTED STARS, AND IT IS
 * MEASURED OFF HER OWN PIXELS RATHER THAN EYEBALLED (R25/R180). <<<
 *
 * HER ART-DIRECTION PASS, CLAUSE 4: *"two-line reviews collide with the painted quotation mark and
 * stars; nudge the text block to clear them at BOTH one-line and two-line lengths."*
 *
 * Scanned down each slot's own column of `membership.png` counting inked pixels per row, her
 * painted furniture resolves into four bands (slots 2 and 3, which carry no filler text):
 *
 *     76.20% .. 77.58%   the slot's top rule      (~290px wide -- the frame)
 *     77.90% .. 79.70%   THE QUOTATION MARK       (~24px wide  -- centred, small)
 *     83.95% .. 85.33%   THE FIVE STARS           (~71px wide  -- centred)
 *     86.40% .. 87.46%   the slot's bottom rule   (~316px wide -- the frame)
 *
 * So the band that clears BOTH is **79.70%..83.95%**, and the box below sits inside it with a hair
 * at each end. What shipped was `top: 79.60, height: 4.60` -- 79.60..84.20 -- which crossed 0.10%
 * into her quotation mark at the top and 0.25% into her stars at the bottom, before the type ever
 * overflowed it. The second half of her clause is in the stylesheet: her review type scaled with the
 * WINDOW while this box scales with her ARTWORK, so at a wide window the words overran the slot
 * whatever it was set to.
 */
export const DESKTOP_REVIEW_SLOTS = Object.freeze([
  Object.freeze({ left: 19.44, top: 79.85, width: 19.91, height: 3.95 }),
  Object.freeze({ left: 40.49, top: 79.85, width: 19.62, height: 3.95 }),
  Object.freeze({ left: 61.36, top: 79.85, width: 19.74, height: 3.95 }),
]);

export const PHONE_REVIEW_SLOTS = Object.freeze([
  Object.freeze({ left: 5.63, top: 85.90, width: 27.85, height: 6.60 }),
  Object.freeze({ left: 35.18, top: 85.90, width: 28.05, height: 6.60 }),
  Object.freeze({ left: 64.82, top: 85.90, width: 28.59, height: 6.60 }),
]);

/**
 * ================================================================================================
 * >>> A QUOTE THAT DOES NOT FIT HER PAINTED SLOT IS NOT OFFERED, RATHER THAN CLIPPED OR SPILLED.
 * ================================================================================================
 *
 * Her slots are small by design — a quotation mark, two or three lines, five painted stars — and her
 * approved pool holds reviews of every length. The three ways to handle that are not equal:
 * **clipping** cuts a member's own words mid-sentence on the page that asks for money, **spilling**
 * puts them across her stars, and **truncating** rewrites what somebody said. So the pool is
 * FILTERED: a quote that fits is offered, one that does not simply is not, and if none fits the slot
 * draws nothing and her artwork shows through.
 *
 * **The cap is set by the PHONE**, which is the tight one: her phone slot is 27.85% of a 393px
 * screen, so about 105px of line at the size this draws — two lines of roughly forty characters.
 */
/* >>> AND THE DESKTOP CAP IS WHAT KEEPS THE LONGEST APPROVED REVIEW TO **TWO** LINES. <<< Her
 * clause 4 names one-line and two-line lengths as the two cases to clear; three lines is a third,
 * and it does not fit the clean band between her quotation mark and her stars at any width. Her
 * desktop slot is 19.91% of a 1672px artwork -- about 333px -- and her review type is 0.72% of that
 * artwork, so a line holds roughly 55 characters and two hold about 110. 130 was three lines.
 * **The words are never clipped or truncated to make this true**: a quote past the cap is simply
 * not offered, which is this module's own standing ruling one paragraph up. */
export const SLOT_MAX_CHARS = Object.freeze({ desktop: 110, phone: 44 });

/**
 * >>> THE CAP IS PER VIEWPORT, BECAUSE THE BOX IS. <<< Her desktop slot is 19.91% of the width and
 * her phone slot 27.85% of a screen a quarter as wide -- about 285px against 105px -- so ONE cap
 * either wastes two thirds of the desktop slot or spills on the phone. The first rendered pass used
 * one number and her phone slots read as cut off, which is a member cut off mid-sentence on the page
 * that asks for money.
 */
export function slotMaxChars(wide) {
  return wide ? SLOT_MAX_CHARS.desktop : SLOT_MAX_CHARS.phone;
}

export function slotPool(quotes, wide = true) {
  if (!Array.isArray(quotes)) return [];
  const cap = slotMaxChars(wide);
  return quotes.filter((q) => typeof q === "string" && q.trim() && q.trim().length <= cap);
}

export function membershipTiers(wide) {
  return wide ? DESKTOP_TIERS : PHONE_TIERS;
}
export function membershipReviewSlots(wide) {
  return wide ? DESKTOP_REVIEW_SLOTS : PHONE_REVIEW_SLOTS;
}

/**
 * >>> ON A PHONE THE CARD *IS* THE BUTTON, WHICH IS HER OWN WORD. <<<
 *
 * So the TARGET is a different box on the two viewports, and it is asked here rather than at the
 * render, where two spellings of *what a member taps* is how one of them comes to be the wrong box.
 */
export function tierTarget(entry) {
  if (!entry || typeof entry !== "object") return null;
  return entry.control || entry.card || null;
}

/**
 * ================================================================================================
 * >>> WHAT THE CONTROL ON EACH TIER SAYS, AND IT IS FOUR ANSWERS RATHER THAN TWO (R18). <<<
 *
 * `current` is the one her badge clause names: **CURRENTLY SUBSCRIBED**, gold on plum, on the
 * member's own tier. `upgrade` is a rung above the one she holds. `choose` is everything else --
 * a rung below, and every tier at all for somebody who holds none.
 *
 * **AND `unknown` IS THE ONE A NAIVE BUILD LOSES.** The tier arrives from the server, so there is a
 * beat on every load where this page does not know. Read as *she holds nothing*, every card flashes
 * **Choose** at a paying member on her own membership screen -- L5.1's own reported defect, on the
 * one page where being told you are not a member is worst. It draws NO verdict at all until the
 * answer lands.
 * ================================================================================================
 */
export const TIER_ORDER = Object.freeze(["free", "newbie", "apprentice", "vet"]);

export function tierRankOf(tier) {
  const at = TIER_ORDER.indexOf(String(tier || "").trim().toLowerCase());
  return at < 0 ? -1 : at;
}

export function tierControlState(tier, memberTier) {
  const mine = tierRankOf(memberTier);
  const theirs = tierRankOf(tier);
  if (theirs < 0) return "unknown";
  // >>> A TIER THIS PAGE HAS NOT BEEN TOLD ABOUT IS NOT A MEMBER WITH NOTHING (R18/L5.1). <<<
  // `memberTier` is `undefined` until the entitlements answer lands, and `"free"` once it says so --
  // two different facts, and only the second one is an answer. **THE EXPLICIT null/undefined CLAUSE
  // THAT STOOD HERE CAME OUT (R216):** `tierRankOf` folds both to `""` and answers -1, so the rank
  // test below already covered them and the extra line could not change an answer -- which the
  // mutation battery proved by going VACUOUS on it (R63/R134). The distinction is untouched and is
  // exactly this line: an answer that has not arrived ranks -1 and is `unknown`, while `"free"`
  // ranks 0 and is a member who holds nothing.
  if (mine < 0) return "unknown";
  if (mine === theirs) return "current";
  // >>> A MEMBER WHO HOLDS NOTHING IS NOT UPGRADING, AND HER OWN ARTWORK SAYS SO. <<< It reads
  // *"Pick the one that fits"*, and the first rendered pass put UPGRADE on all three cards in front
  // of a stranger her advertising had just paid to bring here -- a word about a membership she does
  // not have, on the one surface whose whole job is selling her the first one.
  if (mine === 0) return "choose";
  // >>> **ITEM 11(b) — A TIER BELOW HERS IS A MOVE DOWN, AND IT WAS READING AS "CHOOSE".** <<< Her
  // ruling: *"members may move DOWN a tier themselves (period-end effective)."* This line answered
  // `choose` in both directions, so a paying Vet tapping the Newbie card went through the same door
  // a stranger does — `/api/stripe/checkout`, which for an existing subscriber opens Stripe's
  // tier-change flow under `always_invoice`. **That would have dropped her to the cheaper tier
  // immediately, taking away a room she had already paid for, and invoiced her on the way out** —
  // the two things her ruling forbids, on a button that already looked live.
  return theirs > mine ? "upgrade" : "downgrade";
}

/** Only three of the four may be tapped: there is nothing to buy on the tier she already holds. */
export function tierControlActs(state) {
  // ITEM 11(b): a move DOWN is tappable too — her ruling is that blocking downgrades breeds
  // chargebacks, and the door it presses is the schedule rather than the checkout.
  return state === "upgrade" || state === "choose" || state === "downgrade";
}

/**
 * Her badge, and the two labels beside it. **NO PRICE AND NO TIER NAME IS WRITTEN HERE**: both are
 * painted into her card, and a second copy is one that drifts the first time she re-prices (R28/R29
 * -- and W1-5 measured what two spellings of a price cost this house once already).
 */
export const TIER_CONTROL_LABELS = Object.freeze({
  current: "Currently subscribed",
  upgrade: "Upgrade",
  choose: "Choose",
  // **THE BUTTON SAYS WHAT A TAP DOES** (ITEM 7's law): *Move down* is the act, and the sentence
  // about WHEN it happens is the server's answer rather than a promise printed on a button.
  downgrade: "Move down",
  unknown: "",
});
export function tierControlLabel(state) {
  return Object.prototype.hasOwnProperty.call(TIER_CONTROL_LABELS, state) ? TIER_CONTROL_LABELS[state] : "";
}

/**
 * ================================================================================================
 * >>> HER THREE REVIEW SLOTS, FADING THROUGH THE APPROVED POOL, STAGGERED. <<<
 *
 * Three slots and one pool, so the law's whole job is that **no two slots ever show the same quote**
 * -- three copies of one review is the surface reading as broken rather than as a rotation. Each
 * slot walks the pool by its own offset, and the offsets are the slot INDEX, so slot 2 is always one
 * ahead of slot 1 whatever the pool's length.
 *
 * **A POOL SMALLER THAN THE SLOTS IS ITS OWN ANSWER (R18):** with two quotes the third slot draws
 * NOTHING rather than repeating one, because an empty slot is her artwork showing through and a
 * duplicate is a defect a stranger can see.
 */
export const REVIEW_SLOT_COUNT = 3;
/** Seconds a quote holds before the next fades in. Slow: this is read while she is deciding. */
export const REVIEW_SLOT_SECONDS = 9;
/** The stagger, so the three never turn over together. */
export const REVIEW_SLOT_STAGGER_MS = 2200;

export function slotQuote(quotes, slot, step) {
  if (!Array.isArray(quotes) || !quotes.length) return null;
  if (!Number.isInteger(slot) || slot < 0 || slot >= REVIEW_SLOT_COUNT) return null;
  // Fewer quotes than slots: the extra slots stay empty rather than repeating (see above).
  if (quotes.length <= slot) return null;
  const n = quotes.length;
  const turn = Number.isFinite(step) ? Math.floor(step) : 0;
  // Each slot advances by the SLOT COUNT so the three never collide, and starts at its own index.
  return quotes[(((slot + turn * REVIEW_SLOT_COUNT) % n) + n) % n] || null;
}

/** The three quotes on screen at one moment. Used by the render AND by the gate, so *what is drawn*
 *  has one answer (R28/R29). */
export function slotQuotes(quotes, step) {
  return Array.from({ length: REVIEW_SLOT_COUNT }, (_, i) => slotQuote(quotes, i, step));
}

/**
 * >>> NO TWO SLOTS MAY EVER SHOW THE SAME QUOTE. <<< Held as a law rather than as a property of the
 * arithmetic above, so a later round that changes the walk has to keep the thing that matters.
 */
export function slotsCollide(quotes, step) {
  const shown = slotQuotes(quotes, step).filter(Boolean);
  return new Set(shown).size !== shown.length;
}

// >>> **WHAT THE PAGE SAYS TO A MEMBER THE GATE JUST REFUSED — HER 2026-08-18 RULING'S ONE REAL
// COST, RE-HOMED RATHER THAN DROPPED.** <<<
//
// Her ruling retires the purple box from every member-facing surface, and that box was carrying one
// thing this page cannot paint: **L15's FROZEN FACE.** A member whose card failed met *WELCOME BACK
// / Your membership is paused* there; her artwork paints one eyebrow for everybody, so without this
// a year-long Vet whose card bounced would land on **CHOOSE YOUR MEMBERSHIP** — the cold wall L15
// spent a whole item removing, restored by the round that kills the box (R213's own defect, one
// surface over).
//
// **IT ANSWERS A CATALOG KEY RATHER THAN A SENTENCE** (ITEM 14b: the house's copy lives in
// `strings.js` in both languages, and this module may not hold English). The key it returns is the
// one the box already used, so nothing is re-worded and no row is minted.
//
// **THE FROZEN FLAG IS PRESENCE, NEVER TRUTHINESS (R53/R136/R137).** It arrives off a 402 payload,
// where `Boolean("false")` is `true` — and a coerced read here tells a paying stranger her
// membership is paused, which is a claim about her money.
//
// **AND ONLY THE FREEZE SPEAKS.** An ordinary refusal names no door here: her artwork's own lede
// already says what a membership opens, and inventing a *you touched the Seal Room* sentence would
// be new member-facing copy she did not order (scope rule 4).
// >>> **WALL-1 — AND THE GIFT IS THE SECOND THING THIS PAGE COULD NOT SAY.** <<<
//
// A member holding a complimentary grant, refused at a door her grant does not open, landed here on
// **CHOOSE YOUR MEMBERSHIP** with nothing anywhere naming the membership she already holds. That is
// the freeze's own defect one payload over, and it is the sentence three tickets in one week
// described as a glitch. Measured: 386 live elite-2026 grants across 277 members, every one at
// `apprentice`, with 104 of them ALSO paying for Vet — so two members with the same words on their
// membership meet the same Vet door and get different answers, which is exactly what #139 reported.
//
// **THE FREEZE OUTRANKS THE GIFT, AND THE TWO CANNOT BOTH BE TRUE ANYWAY.** L15's `frozenNow` is
// `frozen && access.tier === "free"`, and a live grant makes that tier non-free — so a gifted member
// is never frozen. The order is written down rather than relied on, so a later change to either law
// shows her one sentence rather than two contradictory ones.
//
// **PRESENCE, NEVER TRUTHINESS (R53/R136/R137), ON BOTH.** These arrive off a 402 payload where
// `Boolean("false")` is `true`, and a coerced read here tells a paying stranger she is on a gift,
// which is a claim about her money.
export function membershipRefusalKey(lock) {
  if (!lock || typeof lock !== "object") return "";
  if (lock.frozen === true) return "tier.frozen.lede";
  if (lock.gift === true) return "tier.gift.lede";
  return "";
}

// >>> **TWO FACTS, AND THE NEWER ONE WINS (R18).** <<< `note` is what the CHECKOUT just answered —
// something she did seconds ago and is waiting on; the refusal note is why she ARRIVED. A page that
// let the arrival note outrank a live checkout refusal would answer her tap with a sentence about
// something else, which is the same lie one line down (ITEM 10's own ruling on a stale note under
// fresh numbers).
export function membershipNoteKey(lock, checkoutNote) {
  return checkoutNote ? "" : membershipRefusalKey(lock);
}

// =================================================================================================
// >>> **HER ROUND-2 NOTE 4: THE CLOSET REVIEW IS NOT FOR STRANGERS.** <<<
//
// Her words: *"the review mentioning the closet is replaced with a more universally relatable one
// from the approved pool (accuracy/life-change/community class), nobody outside knows what the closet
// means."*
//
// **THE QUOTE IS NOT EDITED AND IT IS NOT UNAPPROVED — IT IS NOT SHOWN ON THIS ONE SURFACE.** Her
// member wrote it and meant it; REV-1's approval still stands and it still belongs everywhere inside
// the house. What it cannot do is carry weight with somebody who has never been in, and this page's
// whole job is somebody who has never been in.
//
// **IT DROPS A QUOTE RATHER THAN REWRITING ONE**, which is the only honest move available: trimming
// the word out would put this house's edit inside a member's sentence, and the exact-quotation law
// forbids that everywhere it appears.
//
// **AND IT CAN NEVER EMPTY HER RAIL (R19).** If every approved quote named an insider surface, the
// filter answers the POOL UNCHANGED rather than nothing: an inside-baseball quote on the landing page
// is a small cost, and a landing page with no member voices at all is the thing this section exists
// to prevent.
// =================================================================================================
// >>> **THE ROSTER IS HER ONE WORD, AND ITS NARROWNESS IS THE FINDING RATHER THAN LAZINESS.** <<<
// The first draft also carried `drawer` and `rail` — my guess at the same class, not her
// instruction. **MEASURED AGAINST HER REAL APPROVED POOL, THAT GUESS DROPPED A GOOD REVIEW**: a
// warm, universal *"this is an amazing space and I am still playing around in it"* went with the
// closet ones because it mentions a drawer in passing. Her note names the closet and nothing else,
// and a filter that quietly eats her members' best words to satisfy a rule nobody asked for is worse
// than the problem. **Another word costs one entry here** the day she says so.
export const LANDER_INSIDER_WORDS = Object.freeze(["closet"]);

/** Does this quote lean on a word only somebody already inside the house would understand? */
export function quoteIsInsider(quote) {
  const text = typeof quote === "string" ? quote.toLowerCase() : "";
  if (!text) return false;
  // Word-boundary matched, so "closeted" or a name containing "rail" is not caught by accident.
  // The boundaries are built with `String.raw` rather than a plain template: `\b` inside an ordinary
  // template literal is a BACKSPACE character (\x08), not a word boundary, and the regex then matches
  // nothing while reading perfectly — which is exactly what this line did on its first run.
  return LANDER_INSIDER_WORDS.some((w) => new RegExp(String.raw`\b` + w + String.raw`s?\b`, "i").test(text));
}

/**
 * The pool this page may draw from. **PRESENCE, NEVER TRUTHINESS**: anything that is not a real
 * non-empty string is not a quote, whatever it is.
 */
export function landerQuotes(quotes) {
  const list = Array.isArray(quotes) ? quotes.filter((q) => typeof q === "string" && q.trim()) : [];
  const outside = list.filter((q) => !quoteIsInsider(q));
  // Her rail is never emptied by this law — see the block above.
  return outside.length ? outside : list;
}

/**
 * 1e (her box, 2026-10-02) — **WHAT THE MEMBERSHIP PAGE QUOTES.** Only a real member's words with her
 * @name (the Living Room name the room itself shows, never a legal name or an address), so a row with
 * no name is not quoted. Newest day first, and within a day the strongest first: one she APPROVED
 * before one only harvested. Her approval is the only judge of strength this house has; ranking by
 * length was tried on production and put members' replies to each other above their praise. The composer's straight marks come off
 * because this page DRAWS its own gold marks, and wearing both is the doubled quotation she saw.
 */
export function landerReviews(rows) {
  const list = (Array.isArray(rows) ? rows : [])
    .filter((r) => r && typeof r.quote === "string" && r.quote.trim() && typeof r.name === "string" && r.name.trim())
    .map((r, i) => ({
      i,
      text: r.quote.trim().replace(/^"([\s\S]*)"$/, (_, inner) => inner).trim(),
      name: r.name.trim(),
      day: typeof r.at === "string" ? r.at.slice(0, 10) : "",
      approved: r.provisional === false,
    }))
    .filter((r) => r.text);
  const kept = new Set(landerQuotes(list.map((r) => r.text)));
  return list
    .filter((r) => kept.has(r.text))
    .sort((a, b) => (b.day.localeCompare(a.day)) || (Number(b.approved) - Number(a.approved)) || (a.i - b.i))
    .map(({ text, name }) => ({ text, name }));
}

// ---------------------------------------------------------------------------
// UPGRADE-NOTE-1 — **THE MOVE UP EXPLAINS ITSELF, BECAUSE THE MOVE DOWN ALREADY DOES.**
//
// >>> **FOUND BY MEASUREMENT, NOT BY REASONING (2026-09-04).** <<< DESK-LAW-1 put the desk backlog on
// her Overview, and the first live read was 22 members waiting with 14 of them past the 48-hour
// promise — **and FOUR of the five at the front door had written in about the same thing:**
//
//     c163  "I have a question about billing"
//     c180  "Hello the payment saying 149 I was told it would be 80"
//     c187  "I paid the difference of $80... then I noticed it said $80 due the first month and then $149"
//     c191  "it says on the 7th I will be billed $149 but I thought if we were in Elite and upgraded
//            to the Vet subscription we would only pay the difference"
//
// **NOTHING IS BROKEN. `PRORATION_BEHAVIOR = "always_invoice"` IS DOING EXACTLY WHAT IT SHOULD:** she
// is invoiced the difference for the rest of this period now, and from her next renewal she pays the
// new tier's own price. Stripe's own screen says so. **THE HOUSE SAID NOTHING BEFORE SHE GOT THERE**,
// so four members in two weeks arrived at that screen surprised and wrote in — a support cost, a
// refund conversation, and in c180's case a member who believes she was told a different number.
//
// **THE ASYMMETRY IS THE DEFECT.** A move DOWN already carries `downgradeMessage`: *"you are moving
// to X when your month runs out, nothing changes before then."* A move UP carried a button and a
// price and not one word about what happens after this month. This is the missing half.
//
// **IT SAYS THE HALF THEY ACTUALLY GOT WRONG.** All four expected *pay the difference* to be
// permanent; every one of them was surprised by the RENEWAL. So the sentence leads with the renewal
// price and does not narrate the proration arithmetic — this house does not restate a number Stripe
// is about to show her, and a sentence about *today* that disagreed with her invoice by a cent would
// be worse than no sentence at all (R25/R73).
// ---------------------------------------------------------------------------

/** The catalog row. Named once, because the surface reads it and the lock asks for it (R28/R29). */
export const UPGRADE_NOTE_KEY = "membership.lander.upgradeNote";

/**
 * The note under a tier card's button, or `""` for a card that needs none.
 *
 * **ONLY THE UPGRADE GETS ONE, AND THAT IS A RULING RATHER THAN A GAP.** `current` has nothing to
 * explain; `choose` is a first membership, where the price on the card IS the price every month and a
 * sentence repeating it is noise; `downgrade` is answered by the SERVER after the tap, with the real
 * date on it, and a second sentence here would be two answers to *what happens when I move* (R28/R29).
 *
 * >>> **NO PRICE, NO NOTE.** <<< The sentence carries `{price}`, and `priceText` is the server's —
 * never spelled in this house (W1-5 measured what two spellings of a price cost once already). If it
 * has not arrived, this answers `""` rather than a sentence with a hole where the number goes: *"from
 * your next renewal, this membership is  a month"* is the one shape that would make her write in
 * about billing, on the surface built to stop her having to.
 */
export function tierControlNote(state, { priceText = "" } = {}) {
  if (state !== "upgrade") return "";
  return String(priceText || "").trim() ? UPGRADE_NOTE_KEY : "";
}
